import type { ProductOverrides } from 'types/cms/blocks/ProductBlock';
import type { ProductMedia } from 'types/cms/pages/ProductPage';
import type { SanityProduct } from 'types/cms/SanityProduct';
import type { Variant } from 'types/product/Variant';

function hasTitle(
  source: { title?: string; subtitle?: string } | null | undefined,
): source is { title: string; subtitle?: string } {
  if (!source) return false;
  if (!source.title) return false;
  return true;
}

interface ResolveProductInfo {
  sanityProduct: SanityProduct;
  variants?: Variant[];
  selectedVariantsIds?: string[];
  productOverrides?: ProductOverrides;
  images?: ProductMedia[];
}

export const resolveProductInfo = ({
  sanityProduct,
  variants,
  selectedVariantsIds,
  productOverrides,
  images,
}: ResolveProductInfo) => {
  const isNonFirstVariantSelected =
    selectedVariantsIds?.length === 1 &&
    variants?.length &&
    selectedVariantsIds[0] !== variants[0].id;

  const variant = isNonFirstVariantSelected
    ? variants?.find((v) => v.id === selectedVariantsIds[0])
    : undefined;

  /* Treat title+subtitle as a pair, do not mix overrides.
   * If a title is present but the subtitle is undefined
   * then do not use the subtitle. */
  const getTitleConfig = () => {
    if (hasTitle(productOverrides)) return productOverrides;
    if (hasTitle(sanityProduct.altTitle)) return sanityProduct.altTitle;

    return { title: sanityProduct.title, subtitle: variant?.title };
  };

  const description =
    productOverrides?.description || sanityProduct.longDescription || '';

  /* Overrides the first 2 images if present. */
  const baseImages = images || sanityProduct.images || [];
  const resolvedImages = productOverrides?.media?.length
    ? [
        ...productOverrides.media,
        ...baseImages.slice(productOverrides.media.length),
      ]
    : baseImages;

  const { title, subtitle } = getTitleConfig();

  return {
    title,
    subtitle,
    description,
    images: resolvedImages,
  };
};
